/**
 * Solution Pipeline chart — the "Pipeline" section of the Solution Forecast tab.
 *
 * Hand-rolled SVG bar chart mirroring the SolutionWorkloadChart / FitRadar
 * idiom (no chart library exists in this stack and none may be added). One bar
 * per future Period; bar height = summed pipeline dollars for that period
 * across every pipeline-stage Deal on the Solution (DealStage.IsPipeline = 1).
 * A per-period hover tooltip breaks the total down by Deal.
 *
 * Data comes from the studio-authored "Solution Pipeline (by period)" Gateway
 * view via `useSolutionPipeline`. Degrades gracefully: distinct empty states
 * for "not linked to NOS", "Gateway unavailable", and "no future pipeline".
 *
 * Read-only: never writes, never touches Fit Score / Capacity / points.
 */

import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { type SolutionPipelinePeriod, useSolutionPipeline } from "@/hooks/use-solution-pipeline";

const CHART_W = 640;
const CHART_H = 220;
const PAD_LEFT = 44;
const PAD_RIGHT = 12;
const PAD_TOP = 16;
const PAD_BOTTOM = 34;
const PLOT_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = CHART_H - PAD_TOP - PAD_BOTTOM;
const MAX_BAR_W = 40;

/** Horizontal gridline fractions of yMax — also the Y-axis dollar-tick positions. */
const Y_TICKS = [0, 0.25, 0.5, 0.75, 1] as const;

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Compact USD: $1.2M / $940k / $312. Whole-dollar under 1k. */
function formatUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
}

/** Axis label from a period's ISO start date, e.g. "Apr '26". Falls back to the label. */
function axisLabel(period: SolutionPipelinePeriod): { abbr: string; year: string } {
  if (period.startDate) {
    const d = new Date(period.startDate);
    if (!Number.isNaN(d.getTime())) {
      return {
        abbr: MONTH_ABBR[d.getUTCMonth()] ?? "",
        year: `'${String(d.getUTCFullYear()).slice(2)}`,
      };
    }
  }
  if (period.month !== null && period.year !== null) {
    return { abbr: MONTH_ABBR[period.month - 1] ?? "", year: `'${String(period.year).slice(2)}` };
  }
  return { abbr: period.label.slice(0, 3), year: "" };
}

function x(index: number, count: number): number {
  if (count <= 0) return PAD_LEFT;
  const colW = PLOT_W / count;
  return PAD_LEFT + colW * index + colW / 2;
}

function PipelineSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-[220px] w-full" />
    </div>
  );
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-10 text-center text-[12px] text-muted-foreground">
      {children}
    </div>
  );
}

function PipelineTooltip({ period }: { period: SolutionPipelinePeriod }) {
  return (
    <div className="min-w-[172px] max-w-[240px] rounded-md border border-border bg-card px-2.5 py-2 text-[11px] shadow-md">
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-foreground">{period.label}</span>
        <span className="font-semibold text-foreground tabular-nums">
          {formatUsd(period.amount)}
        </span>
      </div>
      {period.deals.length > 0 ? (
        <div className="mt-1.5 space-y-0.5 border-t border-border pt-1.5">
          {period.deals.map((d) => (
            <div key={d.dealId} className="flex items-center justify-between gap-3">
              <span className="truncate text-muted-foreground">
                {d.dealName}
                <span className="ml-1 text-[10px] text-muted-foreground/70">· {d.stage}</span>
              </span>
              <span className="shrink-0 tabular-nums text-foreground">{formatUsd(d.amount)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-1 text-[10px] text-muted-foreground/70 italic">
          No deals in this period
        </div>
      )}
    </div>
  );
}

/**
 * Keep the floating tooltip inside the chart card (same idiom as
 * SolutionWorkloadChart): left-anchor near the left edge, right-anchor near the
 * right edge, center in the middle.
 */
function tooltipTransform(leftPct: number): string {
  if (leftPct < 28) return "translateX(-12px)";
  if (leftPct > 72) return "translateX(calc(-100% + 12px))";
  return "translateX(-50%)";
}

export function SolutionPipelineChart({ solutionId }: { solutionId: string }) {
  const { data, isLoading, isError, refetch } = useSolutionPipeline(solutionId);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (isLoading) return <PipelineSkeleton />;
  if (isError || !data) {
    return <ErrorState message="Couldn't load the pipeline chart" onRetry={() => refetch()} />;
  }

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Pipeline
      </div>
      {data.linked && !data.unavailable && data.periods.length > 0 && (
        <span className="text-[11px] font-medium text-foreground">
          {formatUsd(data.totalPipeline)} across {data.dealCount}{" "}
          {data.dealCount === 1 ? "deal" : "deals"}
        </span>
      )}
    </div>
  );

  if (!data.linked) {
    return (
      <div className="space-y-3">
        {header}
        <EmptyCard>
          This Solution isn&apos;t linked to a NOS solution yet, so there&apos;s no pipeline to
          show.
        </EmptyCard>
      </div>
    );
  }

  if (data.unavailable) {
    return (
      <div className="space-y-3">
        {header}
        <EmptyCard>
          Pipeline data is unavailable — the Nymbl Gateway isn&apos;t configured or is unreachable
          from this workstation.
        </EmptyCard>
      </div>
    );
  }

  if (data.periods.length === 0) {
    return (
      <div className="space-y-3">
        {header}
        <EmptyCard>No open pipeline in current or future periods for this Solution.</EmptyCard>
      </div>
    );
  }

  const { periods } = data;
  const maxAmount = periods.reduce((max, p) => Math.max(max, p.amount), 0);
  // Headroom so bars never touch the plot ceiling; guard against an all-zero series.
  const yMax = maxAmount > 0 ? maxAmount * 1.15 : 1;
  const colW = PLOT_W / periods.length;
  const barW = Math.min(MAX_BAR_W, colW * 0.6);

  const hovered = hoverIndex !== null ? periods[hoverIndex] : undefined;
  const hoverLeftPct = hoverIndex !== null ? (x(hoverIndex, periods.length) / CHART_W) * 100 : 0;

  return (
    <div className="space-y-3">
      {header}
      <div className="relative rounded-md border border-border bg-card p-2">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="h-56 w-full"
          onMouseLeave={() => setHoverIndex(null)}
        >
          <title>Solution pipeline dollars by future period</title>

          {/* Y-axis: gridlines + dollar tick labels. */}
          <text
            x={PAD_LEFT - 5}
            y={PAD_TOP - 5}
            fontSize="8"
            fill="var(--muted-foreground)"
            textAnchor="end"
            style={{ fontFamily: "var(--font-display)" }}
          >
            $
          </text>
          {Y_TICKS.map((frac) => {
            const gy = PAD_TOP + PLOT_H * (1 - frac);
            return (
              <g key={frac}>
                <line
                  x1={PAD_LEFT}
                  y1={gy}
                  x2={CHART_W - PAD_RIGHT}
                  y2={gy}
                  stroke="var(--border)"
                  strokeWidth="1"
                  strokeDasharray={frac === 0 ? "0" : "2 3"}
                />
                <text
                  x={PAD_LEFT - 5}
                  y={gy + 3}
                  fontSize="8"
                  fill="var(--muted-foreground)"
                  textAnchor="end"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {formatUsd(frac * yMax)}
                </text>
              </g>
            );
          })}

          {/* Hovered-column highlight (behind the bar). */}
          {hoverIndex !== null && (
            <rect
              x={x(hoverIndex, periods.length) - colW / 2}
              y={PAD_TOP}
              width={colW}
              height={PLOT_H}
              fill="var(--foreground)"
              fillOpacity="0.05"
            />
          )}

          {/* Bars: one per period, height = summed pipeline dollars. */}
          {periods.map((p, i) => {
            const frac = yMax > 0 ? Math.min(1, p.amount / yMax) : 0;
            const bh = PLOT_H * frac;
            const by = PAD_TOP + PLOT_H - bh;
            const bx = x(i, periods.length) - barW / 2;
            return (
              <rect
                key={p.periodId}
                x={bx}
                y={by}
                width={barW}
                height={Math.max(0, bh)}
                fill="var(--primary)"
                fillOpacity={hoverIndex === null || hoverIndex === i ? 1 : 0.55}
                rx="2"
              />
            );
          })}

          {/* X-axis: month labels. */}
          {periods.map((p, i) => {
            const { abbr, year } = axisLabel(p);
            return (
              <text
                key={`axis-${p.periodId}`}
                x={x(i, periods.length)}
                y={CHART_H - PAD_BOTTOM + 14}
                fontSize="9"
                fill="var(--muted-foreground)"
                textAnchor="middle"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {abbr}
                {year ? ` ${year}` : ""}
              </text>
            );
          })}

          {/* Transparent per-column hover targets, on top to capture the pointer. */}
          {periods.map((p, i) => (
            <rect
              key={`hover-${p.periodId}`}
              x={x(i, periods.length) - colW / 2}
              y={PAD_TOP}
              width={colW}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
              style={{ cursor: "pointer" }}
            />
          ))}
        </svg>

        {hovered && (
          <div
            className="pointer-events-none absolute top-1 z-10"
            style={{ left: `${hoverLeftPct}%`, transform: tooltipTransform(hoverLeftPct) }}
          >
            <PipelineTooltip period={hovered} />
          </div>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground">
        Forward pipeline only (current and future periods), summed from each pipeline-stage
        deal&apos;s periodized amount. Excludes won, lost, and disqualified deals.
      </p>
    </div>
  );
}
